import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { audiencesAPI } from '../api/audiences';
import { websitesAPI } from '../api/websites';
import './Statistics.css';

function formatLogSummary(log) {
  const ts = log.timestamp ? new Date(log.timestamp).toLocaleString() : '—';
  switch (log.type) {
    case 'click': {
      const classes = log.payload?.classes;
      const detail = log.payload?.elementClass
        ? (Array.isArray(classes) && classes.length
            ? `Element: ${log.payload.elementClass} · Classes: ${classes.join(', ')}`
            : `Element: ${log.payload.elementClass}`)
        : '—';
      return { time: ts, type: 'Click', details: detail };
    }
    case 'lead_submit':
      return {
        time: ts,
        type: 'Lead submit',
        details: log.payload?.formData
          ? Object.keys(log.payload.formData).join(', ')
          : 'Form submitted',
      };
    case 'abandoned_cart':
      return {
        time: ts,
        type: 'Abandoned cart',
        details: log.payload?.formData
          ? Object.keys(log.payload.formData).join(', ')
          : 'Form abandoned',
      };
    case 'page_view':
      return {
        time: ts,
        type: 'Page view',
        details: log.payload?.url ? new URL(log.payload.url).pathname || '—' : '—',
      };
    default:
      return { time: ts, type: log.type || '—', details: '—' };
  }
}

function Statistics() {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const [websiteName, setWebsiteName] = useState('');
  const [audiences, setAudiences] = useState([]);
  const [selectedAudience, setSelectedAudience] = useState(null);
  const [stats, setStats] = useState({ logs: [], visitsByDay: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const buildShowUrl = (log) => {
    const payload = log.payload || {};
    const url = payload.url;
    if (!url) return null;
    if (log.type === 'click' && payload.elementClass) {
      try {
        const u = new URL(url);
        u.hash = 'highlight=' + encodeURIComponent(payload.elementClass);
        return u.toString();
      } catch {
        return url + '#highlight=' + encodeURIComponent(payload.elementClass);
      }
    }
    return url;
  };

  useEffect(() => {
    if (!websiteId) return;
    websitesAPI
      .getById(websiteId)
      .then((w) => setWebsiteName(w.name || ''))
      .catch(() => navigate('/'));
  }, [websiteId, navigate]);

  useEffect(() => {
    if (!websiteId) return;
    audiencesAPI
      .getAll(websiteId)
      .then((data) => {
        setAudiences(data);
        if (data.length > 0 && !selectedAudience) {
          setSelectedAudience(data[0].id);
        }
      })
      .catch((err) => {
        setError(err.message);
      });
  }, [websiteId]);

  useEffect(() => {
    if (!websiteId || !selectedAudience) {
      setStats({ logs: [], visitsByDay: {} });
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    websitesAPI
      .getStats(websiteId, selectedAudience)
      .then(setStats)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [websiteId, selectedAudience]);

  const handleBack = () => navigate(`/editor/${websiteId}`);
  const visitsByDayEntries = Object.entries(stats.visitsByDay || {}).sort(
    (a, b) => b[0].localeCompare(a[0])
  );

  return (
    <div className="statistics-page">
      <header className="statistics-header">
        <button type="button" className="statistics-back" onClick={handleBack}>
          ← Back to editor
        </button>
        <h1 className="statistics-title">
          Statistics {websiteName && `· ${websiteName}`}
        </h1>
      </header>

      <div className="statistics-audience">
        <label htmlFor="stats-audience">Audience</label>
        <select
          id="stats-audience"
          value={selectedAudience || ''}
          onChange={(e) => setSelectedAudience(e.target.value)}
          className="statistics-select"
        >
          {audiences.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </div>

      {error && <div className="statistics-error">{error}</div>}
      {loading && <div className="statistics-loading">Loading…</div>}

      {!loading && !error && (
        <>
          <section className="statistics-section">
            <h2>Page visits over time</h2>
            {visitsByDayEntries.length === 0 ? (
              <p className="statistics-empty">No page visits yet.</p>
            ) : (
              <div className="statistics-visits">
                <table className="statistics-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Visits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visitsByDayEntries.map(([day, count]) => (
                      <tr key={day}>
                        <td>{day}</td>
                        <td>{count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="statistics-section">
            <h2>Activity log (most recent first)</h2>
            {!stats.logs || stats.logs.length === 0 ? (
              <p className="statistics-empty">No activity yet.</p>
            ) : (
              <div className="statistics-logs">
                <table className="statistics-table statistics-table-logs">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Type</th>
                      <th>Details</th>
                      <th className="statistics-th-actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {stats.logs.map((log) => {
                      const { time, type, details } = formatLogSummary(log);
                      const showUrl = buildShowUrl(log);
                      return (
                        <tr key={log.id}>
                          <td>{time}</td>
                          <td>{type}</td>
                          <td>{details}</td>
                          <td className="statistics-log-actions">
                            {showUrl && (
                              <a
                                href={showUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="statistics-show-btn"
                              >
                                Show
                              </a>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default Statistics;
